import React, { useState } from "react";
import { Container } from "reactstrap";
import { PageHeader } from "../../../CommonElements/PageTitle";
import { useTranslation } from "react-i18next";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { useFilter } from "@/CommonElements/TableC/hooks/useFilter";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { usePrivateTransportContractsQuery, useTransportContractRoutesQuery } from "@/services/reports";
import TableC from "@/CommonElements/TableC";
import { LabelValue } from "@/Component/Value";
import { CountModal } from "@/Component/CountModal";

const PrivateTransportContracts = () => {
  const { t } = useTranslation();

  return (
    <div>
      <PageHeader title={t("PRIVATE_TRANSPORT_CONTRACTS")} sticky={false} />
      <Container fluid>
        <PrivateTransportContractsList />
      </Container>
    </div>
  );
};

export default PrivateTransportContracts;



const PrivateTransportContractsList = () => {
  const [page, setPage] = useState(1);
  const per_page = 10;
  const { searchValue, debouncedValue: search, setSearchValue } = useDebouncedSearch(
    "",
    500
  );
  const {
    filters,
    handleFilterApply,
    handleFilterReset, } = useFilter();
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();

  // ==================
  // Side Effect
  // ==================
  const { data: routesRes } = useTransportContractRoutesQuery();
  const routes = routesRes?.data?.data || [];

  const { data: privateTransportContractsRes, isLoading } = usePrivateTransportContractsQuery({
    ...filters,
    search,
    page,
    per_page
  })
  const privateTransportContracts = privateTransportContractsRes?.data?.data || []
  const meta = privateTransportContractsRes?.data?.meta || {}

  const formatDateOnly = (value) => {
    if (!value) return "-";
    if (typeof value === "string") {
      return value.replace("T", " ").split(" ")[0] || "-";
    }
    return String(value).replace("T", " ").split(" ")[0] || "-";
  };

  const columns = [

    {
      name: t("CONTRACT_NUMBER"),
      key: "id",
    },
    {
      name: t("TRANSPORT_COMPANY"),
      key: "provider",
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      name: t("ROUTE"),
      key: "routes",
      render: (value) => {
        const routesList = Array.isArray(value) ? value : [];
        if (routesList.length === 0) return "-";

        return (
          <CountModal
            count={routesList.length}
            modal={{
              header: {
                children: t("ROUTE"),
              },
              body: {
                children: (
                  <ul style={{ paddingInlineStart: "15px", margin: 0 }}>
                    {routesList.map((route) => (
                      <li key={route?.id ?? route?.name_ar ?? route?.name_en}>
                        {getLocalizedText(route?.name_ar, route?.name_en) || "-"}
                      </li>
                    ))}
                  </ul>
                ),
              },
            }}
          >
            {getLocalizedText(routesList[0]?.name_ar, routesList[0]?.name_en) || "-"}
          </CountModal>
        );
      },

    },
    {
      name: t("ORGANIZER"),
      key: "mission",
      render: (value) => getLocalizedText(value?.name_ar, value?.name_en) || "-",
    },
    {
      name: t("CONTRACT_START_DATE"),
      key: "contract_start_date",
      render: (value) => formatDateOnly(value),
    },
    {
      name: t("CONTRACT_END_DATE"),
      key: "contract_end_date",
      render: (value) => formatDateOnly(value),
    },
    {
      name: t("SEATS_COUNT"),
      key: "number_of_pilgrims",
    },
  ]

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPage(1);
  }

  const handlePaginationChange = (page) => {
    setPage(page);
  }


  const filterFields = [
    {
      name: "requester_entity_id",
      label: t("ORGANIZER"),
      type: "select",
      optionKey: "missions",
      options: (mission) => ({
        value: mission.id,
        label: <LabelValue.Value value={[mission.id, getLocalizedText(mission.name_ar, mission.name_en)].join(" - ")} />,
      }),
    },
    {
      name: "company_id",
      label: t("INITIAL_GROUPS_COL_AFFILIATED_ORGANIZER"),
      type: "select",
      optionKey: "companies",
      options: (c) => ({
        value: c.id,
        label: <LabelValue.Value value={[c.id, getLocalizedText(c.name_ar, c.name_en)].join(" - ")} />,
      }),
    },
    {
      name: "provider_entity_id",
      label: t("TRANSPORT_COMPANY"),
      type: "select",
      optionKey: "bus_companies",
      options: (company) => ({
        value: company.id,
        label: <LabelValue.Value value={getLocalizedText(company.name_ar, company.name_en)} />,
      }),
    },
    {
      name: "routes",
      label: t("ROUTE"),
      type: "select",
      options: routes.map((route) => ({
        value: route.id,
        label: [route.id, getLocalizedText(route.name_ar, route.name_en)].join(" - "),
      })),
    },
  ]

  const onFilterApply = () => {
    setPage(1);
  }

  return (

    <TableC
      // basic
      data={privateTransportContracts}
      columns={columns}
      loading={isLoading}
      // search
      searchValue={searchValue}
      onSearchChange={handleSearchChange}
      // pagination
      pagination={{
        current: page,
        total: meta?.total ?? 0,
        pageSize: meta?.per_page ?? 10,
        onChange: handlePaginationChange,
      }}
      // filter
      filterFields={filterFields}
      filters={filters}
      onFilterApply={handleFilterApply(onFilterApply)}
      onFilterReset={handleFilterReset}
    // actions
    // actions={getActions}
    />
  )
}